<template>
  <b-container fluid>
    <group-componentized-module
      v-if="!!isSensorComponentized"
      :options="sensorInformation"
      :selected-information="selectedInformation"
      @on-change-selected-information="onChangeSelectedInformationHandler"
    />
    <b-row v-if="setsMetadatas.length">
      <b-col cols="12" class="text-right">
        <collapse-expand-dropdown
          @click-option="setVisibleAll"
        />
      </b-col>
      <b-col>
        <b-tabs
          :vertical="verticalTabs"
          v-model="currentTab"
          v-if="!loading"
          >
          <b-tab
            v-for="(setMetadata, index) in setsMetadatas"
            :key="index"
            :title="getTitle(setMetadata)"
          >
            <set-main-panel :set-metadata="setMetadata" />
          </b-tab>
        </b-tabs>
      </b-col>
    </b-row>
    <b-row v-else align-h="center">
      <s-empty-list :loading="loading"/>
    </b-row>
  </b-container>
</template>

<script>
import CollapseExpandDropdown from '@/components/buttons/CollapseExpandDropdown';
import loadingMixin from '@/services/loadingMixin';
import optimizedResizeEvent from '@/services/optimizedResizeEvent';
import themeService from '@/services/themeService';
import regulationSetsService from '@/domain/services/installation/supervision/sensors/regulationSets';
import eventBus from '@/services/globalEventBus';
import event from '@/domain/events/engineeringModulesHeader';
import SetMainPanel from '@/domain/components/installation/supervision/sensors/regulationSets/SetMainPanel';
import GroupComponentizedModule from '@/domain/components/installation/supervision/sensors/GroupComponentizedModule';
import { mapGetters } from 'vuex';
import { ModuleCategorys } from 'sigma-mappings';

function createSetMetadata(set) {
  return {
    set,
    baseDataVisible: true,
    timingStepVisible: true,
    hourRangeVisible: true,
  };
}

export default {
  name: 'RegulationSets',
  props: {
    sensor: Object,
    isSensorComponentized: {
      type: Boolean,
      default: false,
    },
    category: String,
    selectedInformation: {
      type: Object,
      default: () => ({}),
    },
    sensorInformation: {
      type: Array,
      default: () => ([]),
    },
  },
  components: {
    CollapseExpandDropdown,
    SetMainPanel,
    GroupComponentizedModule,
  },
  data: () => ({
    setsMetadatas: [],
    verticalTabs: true,
    currentTab: 0,
    statusBeforePrint: null,
  }),
  mixins: [
    loadingMixin,
  ],
  computed: {
    ...mapGetters([
      'isPrinting',
    ]),
    currentMetaData() {
      return this.setsMetadatas[this.currentTab];
    },
    selectedAssetModuleId() {
      const { sensorId } = this.$route.params;
      const { assetModuleId } = this.selectedInformation || {};
      return this.category === ModuleCategorys.COMPONENTIZED
        ? assetModuleId
        : sensorId;
    },
  },
  methods: {
    onBeforePrint() {
      this.statusBeforePrint = { ...this.currentMetaData };
      if (!this.statusBeforePrint) return;
      this.$nextTick(() => {
        this.openAccordions(true);
      });
    },
    onAfterPrint() {
      if (!this.statusBeforePrint) return;
      this.$nextTick(() => {
        this.restoreAccordionState();
      });
    },
    openAccordions(value) {
      this.currentMetaData.baseDataVisible = value;
      this.currentMetaData.timingStepVisible = value;
      this.currentMetaData.hourRangeVisible = value;
    },
    restoreAccordionState() {
      this.currentMetaData.baseDataVisible = this.statusBeforePrint.baseDataVisible;
      this.currentMetaData.timingStepVisible = this.statusBeforePrint.timingStepVisible;
      this.currentMetaData.hourRangeVisible = this.statusBeforePrint.hourRangeVisible;
      this.statusBeforePrint = null;
    },
    init() {
      optimizedResizeEvent.on(this.onResize, this);
      this.refreshData();
      eventBus.on(event.refresh, this.refreshDataEvent, this);
    },
    deinit() {
      optimizedResizeEvent.off(this.onResize, this);
      eventBus.off(event.refresh, this.refreshDataEvent, this);
    },
    onResize() {
      const newValue = themeService.isScreenSize('min-md');
      if (newValue !== this.verticalTabs) {
        this.verticalTabs = newValue;
      }
    },
    refreshDataEvent({ wait }) {
      wait(this.refreshData());
    },
    async refreshData() {
      this.startLoading();
      try {
        this.setsMetadatas = (await regulationSetsService.getRegulationSets(
          this.selectedAssetModuleId,
        )).map(createSetMetadata);
      } finally {
        this.endLoading();
      }
    },
    getTitle(setMetadata) {
      return this.$t('sensors.regulation-sets.set-title', {
        name: setMetadata.set.number,
      });
    },
    setVisibleAll(visible) {
      this.setsMetadatas.forEach((setMetadata) => {
        this.$set(setMetadata, 'baseDataVisible', visible);
        this.$set(setMetadata, 'timingStepVisible', visible);
        this.$set(setMetadata, 'hourRangeVisible', visible);
      });
    },
    onChangeSelectedInformationHandler(value) {
      this.$emit('on-change-selected-information', value);
    },
  },
  created() {
    this.init();
  },
  beforeDestroy() {
    this.deinit();
  },
  watch: {
    isPrinting(newValue, oldValue) {
      if (newValue && !oldValue) {
        this.onBeforePrint();
      }
      if (!newValue && oldValue) {
        this.onAfterPrint();
      }
    },
  },
};
</script>

<style lang="sass" scoped>
::v-deep
  ul.nav-tabs
    border-bottom: transparent
</style>
